import React, { forwardRef } from 'react';
import clsx from 'clsx';

/**
 * Button — the single button primitive for the whole app.
 *
 * Variants:
 *   primary    — main CTA (Soft Olive)
 *   secondary  — complementary CTA (Warm Sand)
 *   outline    — outlined on white
 *   subtle     — quiet neutral fill
 *   ghost      — minimal, hover-only background
 *   success / warning / danger — semantic
 *   link       — text-only with underline on hover
 *
 * Sizes: xs, sm, md, lg, xl
 *
 * Always pass `icon` as a lucide-react component (or any node).
 * Direction (`iconPosition`) auto-flips correctly inside RTL contexts via flex.
 */
const VARIANTS = {
    primary:   'bg-brand-500 text-ink-inverse border-brand-600/15 hover:bg-brand-600 active:bg-brand-700 shadow-sm hover:shadow-md',
    secondary: 'bg-secondary-500 text-ink-inverse border-secondary-600/15 hover:bg-secondary-600 active:bg-secondary-700 shadow-sm hover:shadow-md',
    outline:   'bg-surface text-brand-700 border-brand-300 hover:bg-brand-50 hover:border-brand-400 active:bg-brand-100',
    subtle:    'bg-surface-muted text-ink border-line hover:bg-surface-sunken active:bg-line',
    ghost:     'bg-transparent text-ink border-transparent hover:bg-surface-muted active:bg-surface-sunken',
    success:   'bg-success-500 text-ink-inverse border-success-600/15 hover:bg-success-600 active:bg-success-700 shadow-sm hover:shadow-md',
    warning:   'bg-warning-500 text-ink-inverse border-warning-600/15 hover:bg-warning-600 active:bg-warning-700 shadow-sm hover:shadow-md',
    danger:    'bg-danger-500 text-ink-inverse border-danger-600/15 hover:bg-danger-600 active:bg-danger-700 shadow-sm hover:shadow-md',
    link:      'bg-transparent text-brand-700 border-transparent hover:text-brand-800 hover:underline underline-offset-4 px-0 py-0 shadow-none',
};

const SIZES = {
    xs: { padding: 'px-2.5 py-1.5',  text: 'text-xs',  gap: 'gap-1.5', icon: 'w-3.5 h-3.5', radius: 'rounded-md' },
    sm: { padding: 'px-3.5 py-2',    text: 'text-sm',  gap: 'gap-1.5', icon: 'w-4 h-4',     radius: 'rounded-md' },
    md: { padding: 'px-5 py-2.5',    text: 'text-sm',  gap: 'gap-2',   icon: 'w-4 h-4',     radius: 'rounded-lg' },
    lg: { padding: 'px-6 py-3',      text: 'text-base',gap: 'gap-2',   icon: 'w-5 h-5',     radius: 'rounded-lg' },
    xl: { padding: 'px-7 py-3.5',    text: 'text-lg',  gap: 'gap-2.5', icon: 'w-5 h-5',     radius: 'rounded-xl' },
};

export const Button = forwardRef(function Button({
    children,
    variant = 'primary',
    size = 'md',
    className,
    disabled = false,
    loading = false,
    icon,
    iconPosition = 'start',
    fullWidth = false,
    pill = false,
    type = 'button',
    onClick,
    as: Component = 'button',
    ...rest
}, ref) {
    const sz = SIZES[size] || SIZES.md;
    const isLink = variant === 'link';

    const base = clsx(
        'inline-flex items-center justify-center select-none whitespace-nowrap',
        'font-semibold leading-none',
        'border',
        'transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-smooth',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        !isLink && 'active:scale-[0.98]',
        fullWidth && 'w-full',
        pill ? 'rounded-pill' : sz.radius,
    );

    const renderIcon = () => icon && (
        <span className={clsx('inline-flex items-center justify-center shrink-0', sz.icon)} aria-hidden="true">
            {icon}
        </span>
    );

    return (
        <Component
            ref={ref}
            type={Component === 'button' ? type : undefined}
            onClick={onClick}
            disabled={Component === 'button' ? (disabled || loading) : undefined}
            aria-busy={loading || undefined}
            className={clsx(
                base,
                VARIANTS[variant] || VARIANTS.primary,
                !isLink && sz.padding,
                !isLink && sz.text,
                sz.gap,
                className,
            )}
            {...rest}
        >
            {loading ? (
                <>
                    <svg className={clsx('animate-spin', sz.icon)} fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {children && <span className="opacity-80">{children}</span>}
                </>
            ) : (
                <>
                    {iconPosition === 'start' && renderIcon()}
                    {children}
                    {iconPosition === 'end' && renderIcon()}
                </>
            )}
        </Component>
    );
});

export default Button;
